import axios from 'axios';

/**
 * SYSTEM PROMPT: Implement Runtime Backend Drift Watchdog
 * OBJECTIVE: Ensure that ALL outgoing network requests are validated in real-time.
 */

/**
 * 🛠️ STEP 3 — VALIDATION FUNCTION
 */
function validateEndpoint(url: string | undefined, env: any) {
    const isProd = env.VITE_PROD_MODE === 'true';

    if (!url) return;

    // 🛡️ BLOCK CLOUD IN DEV
    if (!isProd && url.includes('run.app')) {
        console.error(`%c[WATCHDOG] BLOCKED CLOUD CALL IN DEV: ${url}`, 'color: #ef4444; font-weight: bold;');
        throw new Error('[RUNTIME BLOCK] Cloud backend call blocked in DEV mode. Development must use local backend.');
    }

    // 🛡️ BLOCK LOCAL IN PROD
    if (isProd && (url.includes('localhost') || url.includes('127.0.0.1'))) {
        console.error(`%c[WATCHDOG] BLOCKED LOCAL CALL IN PROD: ${url}`, 'color: #ef4444; font-weight: bold;');
        throw new Error('[RUNTIME BLOCK] Local backend call blocked in PROD mode. Production must use cloud gateway.');
    }

    // ✅ PASS
    if (url.includes('run.app') || url.includes('localhost') || url.includes('127.0.0.1')) {
        console.log(`%c[WATCHDOG] Request allowed: ${url}`, 'color: #22c55e;');
    }
}

/**
 * 🛠️ STEP 1 — INTERCEPT FETCH (MANDATORY)
 */
function interceptFetch(env: any) {
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
        const url = args[0]?.toString();
        validateEndpoint(url, env);
        return originalFetch(...args);
    };
}

/**
 * 🛠️ STEP 2 — AXIOS INTERCEPTOR
 */
function interceptAxios(env: any) {
    axios.interceptors.request.use((config) => {
        validateEndpoint(config.url, env);
        return config;
    });
}

/**
 * 🛠️ STEP 1 (Revised) — STARTUP VALIDATION
 */
export function performRoutingValidation(env: any) {
    const isProd = env.VITE_PROD_MODE === 'true';
    const gateway = env.VITE_GATEWAY_URL;

    console.log('%c[ENV CHECK]', 'color: #3b82f6; font-weight: bold;');
    console.log('Mode:', isProd ? 'PRODUCTION' : 'DEVELOPMENT');
    console.log('Backend:', gateway);

    if (!gateway) {
        throw new Error('[CONFIG ERROR] VITE_GATEWAY_URL is missing');
    }

    if (!isProd && gateway.includes('run.app')) {
        throw new Error('[DRIFT BLOCKED] Development mode cannot use Cloud backend');
    }

    if (isProd && (gateway.includes('127.0.0.1') || gateway.includes('localhost'))) {
        throw new Error('[DRIFT BLOCKED] Production mode cannot use Local backend');
    }

    if (env.MODE === 'production' && !gateway.includes('run.app')) {
         throw new Error('[BUILD BLOCKED] Production build requires cloud backend');
    }
}

/**
 * 🛠️ STEP 4 — GLOBAL INSTALL
 */
export function installRuntimeWatchdog(env: any) {
    interceptFetch(env);
    interceptAxios(env);
    console.info('%c[Watchdog] Runtime interceptors active (Fetch + Axios).', 'color: #3b82f6; font-style: italic;');
}

/**
 * ENTRY POINT
 */
export function validateRouting() {
    performRoutingValidation(import.meta.env);
    installRuntimeWatchdog(import.meta.env);
}
